import { supabase } from '../lib/supabase';

/**
 * Upload a document to Supabase storage
 * @param {File} file - The file to upload
 * @param {string} userId - The user's ID
 * @param {string} documentType - Type of document ('medical_license' or 'clinical_affiliation')
 * @returns {Promise<{url: string, path: string}>} - Document URL and storage path
 */
export const uploadDocument = async (file, userId, documentType) => {
  try {
    if (!file || !userId || !documentType) {
      throw new Error('Missing required parameters for document upload');
    }

    // Generate unique filename with timestamp
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${documentType}_${Date.now()}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    // Upload file to Supabase storage
    const { data, error } = await supabase?.storage?.from('user-documents')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    // Insert document record into database
    const { data: docRecord, error: dbError } = await supabase?.from('user_documents')?.insert({
        user_id: userId,
        document_type: documentType,
        document_url: data?.path,
        file_name: file?.name,
        file_size: file?.size
      })?.select()?.single();

    if (dbError) {
      // Clean up storage if database insert fails
      await supabase?.storage?.from('user-documents')?.remove([data?.path]);
      throw dbError;
    }

    return {
      url: data?.path,
      path: data?.path,
      documentId: docRecord?.id
    };
  } catch (error) {
    console.error('Error uploading document:', error);
    throw error;
  }
};

/**
 * Get signed URL for a private document
 * @param {string} documentPath - Storage path of the document
 * @returns {Promise<string>} - Signed URL
 */
export const getDocumentUrl = async (documentPath) => {
  try {
    const { data, error } = await supabase?.storage?.from('user-documents')?.createSignedUrl(documentPath, 3600); // 1 hour expiry

    if (error) throw error;
    return data?.signedUrl;
  } catch (error) {
    console.error('Error getting document URL:', error);
    throw error;
  }
};

/**
 * Get all documents for a user
 * @param {string} userId - The user's ID
 * @returns {Promise<Array>} - Array of document records
 */
export const getUserDocuments = async (userId) => {
  try {
    const { data, error } = await supabase?.from('user_documents')?.select('*')?.eq('user_id', userId)?.order('uploaded_at', { ascending: false });

    if (error) throw error;

    // Get signed URLs for all documents
    const documentsWithUrls = await Promise.all(
      data?.map(async (doc) => {
        try {
          const signedUrl = await getDocumentUrl(doc?.document_url);
          return { ...doc, signedUrl };
        } catch (err) {
          console.error(`Error getting URL for document ${doc?.id}:`, err);
          return { ...doc, signedUrl: null };
        }
      })
    );

    return documentsWithUrls;
  } catch (error) {
    console.error('Error fetching user documents:', error);
    throw error;
  }
};

/**
 * Verify or reject a document (admin only)
 * @param {string} documentId - Document ID
 * @param {string} status - 'verified' or 'rejected'
 * @param {string} notes - Verification notes
 * @param {string} adminId - Admin user ID
 * @returns {Promise<object>} - Updated document record
 */
export const verifyDocument = async (documentId, status, notes, adminId) => {
  try {
    const { data, error } = await supabase?.from('user_documents')?.update({
        verified_at: status === 'verified' ? new Date()?.toISOString() : null,
        verified_by: status === 'verified' ? adminId : null,
        verification_notes: notes
      })?.eq('id', documentId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error verifying document:', error);
    throw error;
  }
};

/**
 * Get all pending document approvals (admin only)
 * @returns {Promise<Array>} - Array of users with pending documents
 */
export const getPendingDocumentApprovals = async () => {
  try {
    // Get users with pending verification status - NOW INCLUDING ALL NECESSARY FIELDS
    const { data: users, error: usersError } = await supabase
      ?.from('user_profiles')
      ?.select(`
        id,
        full_name,
        email,
        phone_number,
        license_number,
        specialization,
        institution_name,
        institution_country,
        verification_status,
        created_at
      `)
      ?.eq('verification_status', 'pending')
      ?.order('created_at', { ascending: false });

    if (usersError) throw usersError;

    // Get documents for each user
    const usersWithDocs = await Promise.all(
      users?.map(async (user) => {
        const docs = await getUserDocuments(user?.id);
        return { ...user, documents: docs };
      })
    );

    return usersWithDocs;
  } catch (error) {
    console.error('Error fetching pending approvals:', error);
    throw error;
  }
};

/**
 * Approve or reject user application
 * @param {string} userId - User ID
 * @param {string} status - 'verified' or 'rejected'
 * @param {string} notes - Admin notes
 * @returns {Promise<object>} - Updated user profile
 */
export const updateUserVerificationStatus = async (userId, status, notes = '') => {
  try {
    const updateData = {
      verification_status: status,
      verified_at: status === 'verified' ? new Date()?.toISOString() : null,
      role: status === 'verified' ? 'clinician' : 'pending'
    };

    const { data, error } = await supabase?.from('user_profiles')?.update(updateData)?.eq('id', userId)?.select()?.single();

    if (error) throw error;

    // Update all documents for this user
    const { error: docsError } = await supabase?.from('user_documents')?.update({
        verified_at: status === 'verified' ? new Date()?.toISOString() : null,
        verification_notes: notes
      })?.eq('user_id', userId);

    if (docsError) throw docsError;

    const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('email, full_name, phone_number, license_number, institution_country, specialization, institution_name, years_of_practice')?.eq('id', userId)?.single();

    const { data: documents, error: docsError2 } = await supabase?.from('user_documents')?.select('*')?.eq('user_id', userId)?.order('uploaded_at', { ascending: false });

    if (docsError2) throw docsError2;

    return { profile, documents };
  } catch (error) {
    console.error('Error updating verification status:', error);
    throw error;
  }
};

// Add new function for sending verification emails
export const sendVerificationEmail = async (email, name, status, reason = null) => {
  try {
    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-verification-email`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          email,
          name,
          status,
          reason
        })
      }
    );

    if (!response?.ok) {
      const errorData = await response?.json();
      throw new Error(errorData?.error || 'Failed to send verification email');
    }

    const data = await response?.json();
    return data;
  } catch (error) {
    console.error('Error sending verification email:', error);
    throw error;
  }
};

function documentService(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: documentService is not implemented yet.', args);
  return null;
}

export { documentService };